import Css from "./style.module.scss";

import * as Icons from "@phosphor-icons/react";
import { checkIsBusinessUser } from "selectors/user";
import { getSelectedBusinessBookCloseDate, getSelectedBusinessIntegrationServiceData } from "selectors/businesses";
import { getTextsData } from "selectors/texts";
import { useDispatch, useSelector } from "react-redux";
import ActionCellContent from "nlib/common/ActionCellContent";
import DataStatuses from "const/DataStatuses";
import DateTimeFormats from "const/DateTimeFormats";
import DocumentRecogniseStatuses from "const/DocumentRecogniseStatuses";
import DocumentsActions from "actions/DocumentsActions";
import Files from "utils/Files";
import MainApiActions from "actions/MainApiActions";
import React, { useCallback, useMemo } from "react";
import Texts from "utils/Texts";
import moment from "moment";
import useShowCommonModal from "hooks/useShowCommonModal";

const ACTIONS = {
  EDIT: "edit",
  OPEN: "open",
  DOWNLOAD: "download",
  DELETE: "delete"
};

const ActionsCell = (props) => {
  const {
    disableApprove,
    documentData,
    refetchDocuments,
    openDocument,
    onApprove
  } = props;

  const dispatch = useDispatch();

  const showCommonModal = useShowCommonModal();

  const businessUser = useSelector(checkIsBusinessUser);

  const selectedBusinessBookCloseDate = useSelector(getSelectedBusinessBookCloseDate);

  const { label: integrationServiceLabel } = useSelector(getSelectedBusinessIntegrationServiceData) || {};

  const { uiTexts, messages } = useSelector(getTextsData);

  const {
    id: documentId,
    status,
    issueDate,
    attachment = {},
    recogniseData = {}
  } = documentData;

  const closedBookDate = selectedBusinessBookCloseDate && issueDate
    && moment.utc(issueDate).isSameOrBefore(moment.utc(selectedBusinessBookCloseDate));

  const showDeleteTooltip = status === DataStatuses.EXPORTED && !attachment?.key;

  const disableDelete = ((status === DataStatuses.TO_EXTRACT) && (recogniseData.status !== DocumentRecogniseStatuses.ERROR))
    || showDeleteTooltip;

  const menuItems = useMemo(() => {
    const toExtractStatus = status === DataStatuses.TO_EXTRACT;

    const warningApprove = !toExtractStatus && !disableApprove && closedBookDate;

    return [
      !businessUser && {
        theme: warningApprove ? "warning" : "success",
        text: uiTexts.approve,
        icon: warningApprove ? Icons.Warning : Icons.Check,
        action: "approve",
        disabled: toExtractStatus || disableApprove
      },
      {
        action: status === DataStatuses.TO_REVIEW ? ACTIONS.EDIT : ACTIONS.OPEN,
        disabled: toExtractStatus
      },
      {
        action: ACTIONS.DOWNLOAD,
        disabled: !attachment?.key
      },
      {
        disabled: disableDelete,
        action: ACTIONS.DELETE,
        dataTooltip: showDeleteTooltip
          ? Texts.replaceVars(messages.documentDeleteWarning, { service: integrationServiceLabel })
          : undefined,
        dataTooltipPosition: "left"
      }
    ].filter(Boolean);
  }, [
    integrationServiceLabel,
    showDeleteTooltip,
    attachment?.key,
    disableApprove,
    closedBookDate,
    disableDelete,
    businessUser,
    messages,
    uiTexts,
    status
  ]);

  const handleMenuItemClick = useCallback(async(action) => {
    switch (action) {
      case "approve":
        if (closedBookDate) {
          showCommonModal({
            text: Texts.replaceVars(messages.closeBookWarningForDocs, {
              date: moment.utc(selectedBusinessBookCloseDate).format(DateTimeFormats.DATE_TEXT_EXT),
              service: integrationServiceLabel
            }),
            headerText: uiTexts.warning
          });
        } else {
          onApprove(documentId);
        }
        break;
      case "edit":
      case "open":
        openDocument(documentId);
        break;
      case "download": {
        const link = await dispatch(MainApiActions.fetchAttachmentUrl(attachment, true));

        if (link) Files.downloadContent(link);
        break;
      }
      case "delete": {
        const modalResult = await showCommonModal({ text: messages.documentDeleteConfirm, confirm: true });

        if (modalResult) {
          const successful = await dispatch(DocumentsActions.deleteDocument(documentId));

          if (successful) refetchDocuments();
        }
        break;
      }
      default:
        break;
    }
  }, [
    integrationServiceLabel,
    selectedBusinessBookCloseDate,
    closedBookDate,
    documentId,
    attachment,
    messages,
    uiTexts,
    dispatch,
    onApprove,
    openDocument,
    showCommonModal,
    refetchDocuments
  ]);

  return (
    <div className={Css.actionsCell}>
      <ActionCellContent
        menuItems={menuItems}
        onClick={handleMenuItemClick} />
    </div>
  );
};

export default React.memo(ActionsCell);
